import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Bell, Inbox, FileBadge, BadgeCheck } from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from './ui/dropdown-menu';

const API = process.env.REACT_APP_BACKEND_URL;

const TYPE_META = {
  salary_certificate: { label: 'Salary Certificate', icon: FileBadge, color: 'text-emerald-600' },
  experience_certificate: { label: 'Experience Certificate', icon: BadgeCheck, color: 'text-sky-600' },
};

const timeAgo = (d) => {
  if (!d) return '';
  const s = Math.floor((Date.now() - new Date(d).getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

const HeaderNotificationBell = () => {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const timer = useRef(null);

  const load = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const r = await axios.get(`${API}/api/document-requests?status=pending`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRequests(Array.isArray(r.data) ? r.data : []);
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    load();
    timer.current = setInterval(load, 60000);
    return () => clearInterval(timer.current);
  }, [load]);

  const count = requests.length;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="relative p-2 rounded-full hover:bg-gray-100 transition-colors" data-testid="hr-notification-bell" aria-label="Notifications">
          <Bell className="h-5 w-5 text-gray-600" />
          {count > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center" data-testid="bell-badge">
              {count > 9 ? '9+' : count}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80" data-testid="bell-dropdown">
        <DropdownMenuLabel className="flex items-center gap-2">
          <Inbox className="h-4 w-4 text-violet-600" /> Document Requests
          {count > 0 && <span className="ml-auto text-xs text-gray-400">{count} pending</span>}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {count === 0 ? (
          <div className="py-6 text-center text-sm text-gray-400">No new requests</div>
        ) : (
          requests.slice(0, 6).map((req) => {
            const meta = TYPE_META[req.request_type] || {};
            const Icon = meta.icon || FileBadge;
            return (
              <DropdownMenuItem
                key={req.id}
                className="flex items-start gap-2 cursor-pointer py-2"
                onClick={() => navigate('/dashboard')}
                data-testid={`bell-item-${req.id}`}
              >
                <Icon className={`h-4 w-4 mt-0.5 flex-shrink-0 ${meta.color || 'text-gray-500'}`} />
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-800 truncate">{req.employee_name}</p>
                  <p className="text-xs text-gray-500 truncate">
                    {meta.label || req.request_label}{req.note ? ` · ${req.note}` : ''}
                  </p>
                  <p className="text-[10px] text-gray-400">{timeAgo(req.created_at)}</p>
                </div>
              </DropdownMenuItem>
            );
          })
        )}
        {count > 0 && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem className="justify-center text-violet-600 font-medium cursor-pointer" onClick={() => navigate('/dashboard')} data-testid="bell-view-all">
              View all on dashboard
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default HeaderNotificationBell;
